'use client';

import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from 'react';
import { useForm, FieldValues } from 'react-hook-form';
import classnames from 'classnames';

import { FormContext } from './FormContext';

import Box from '../../atoms/Box/Box';
import Container from '../../atoms/Grid/Container/Container';
import Spec from '../../atoms/Spec/Spec';
import Loader from '../../atoms/Loader/Loader';

import { UTMAttributionHistory } from '../../../utils/UTMAttribution';
import {
  getDefaultValues,
  isEmpty,
  getEffectiveWidth,
  observeInputChanges,
  sanitizeData,
} from './utils';

import { scrollToFirstError } from './helpers/scrollHelpers';
import {
  pushToDataLayer,
  trackProcessAbandon,
  trackFieldInteraction,
} from './helpers/analyticsHelpers';

// Form Controls
import InputControl from './controls/InputControl';
import TextAreaControl from './controls/TextAreaControl';
import PhoneControl from './controls/PhoneControl';
import CheckboxControl from './controls/CheckboxControl';
import CheckboxGroupControl from './controls/CheckboxGroupControl';
import RadioGroupControl from './controls/RadioGroupControl';
import DropdownControl from './controls/DropdownControl';
import DatePickerControl from './controls/DatePickerControl';
import DisclaimersControl from './controls/DisclaimersControl';
import ReCaptchaControl from './controls/ReCaptchaControl';
import ButtonControl from './controls/ButtonControl';
import ButtonGroupControl from './controls/ButtonGroupControl';
import ContentControl from './controls/ContentControl';
import GroupControl from './controls/GroupControl';
import FileUploadControl from './controls/FileUploadControl';
import CustomControl from './controls/CustomControl';
import CurrencyControl from './controls/CurrencyControl';
import TypeaheadControl from './controls/TypeaheadControl';

import styles from './Form.module.scss';

// Re-export all types from types.ts for backwards compatibility
export type {
  FormFieldBase,
  InputField,
  TextAreaField,
  PhoneField,
  CheckboxField,
  CheckboxGroupField,
  RadioGroupOption,
  RadioGroupField,
  DropdownField,
  DatePickerField,
  DisclaimersField,
  ReCaptchaField,
  ButtonField,
  ButtonGroupButton,
  ButtonGroupField,
  ContentField,
  FileUploadField,
  BaseFormField,
  GroupField,
  FormField,
  FormProps,
  FormRef,
  FormFieldWidth,
  FormFieldDependency,
  FormFieldWidthWhen,
  ValidationMessages,
  CheckboxGroupOption,
  DropdownOption,
  DatePickerLocale,
  ButtonVariant,
  ButtonColor,
  ButtonSize,
  PostalCodeAutoPopulate,
  ButtonAlign,
  ContentVariant,
  ContentColor,
  PhoneInputCountryCode,
  UploadUrlResponse,
  TypeaheadField,
} from './types';

// Re-export FormContext for external use
// eslint-disable-next-line react-refresh/only-export-components -- Intentional re-export for public API
export { FormContext, useFormContext } from './FormContext';
export type { FormContextValue } from './FormContext';

import type {
  FormField,
  FormProps,
  FormRef,
  InputField,
  TextAreaField,
  PhoneField,
  CheckboxField,
  CheckboxGroupField,
  RadioGroupField,
  DropdownField,
  DatePickerField,
  CustomField,
  DatePickerLocale,
  DisclaimersField,
  ReCaptchaField,
  ButtonField,
  ButtonGroupField,
  ContentField,
  GroupField,
  FileUploadField,
  CurrencyField,
  TypeaheadField,
} from './types';

/**
 * A flexible form component that supports various field types and handles submission
 * @component
 * @param {FormProps} props - Form configuration and handlers
 * @returns {JSX.Element} Form component
 * @example
 * <Form
 *   id="contact-form"
 *   formFields={fields}
 *   onSuccess={(data) => handleSuccess(data)}
 *   theme="dark"
 * />
 */
const Form = forwardRef<FormRef, FormProps>(
  (
    {
      id,
      formFields,
      apiEndpoint,
      theme = 'dark',
      onSuccess,
      onError,
      onDirtyChange,
      payload,
      alignment = 'center',
      successMessage,
      errorMessage,
      direction = 'ltr',
      isFluid = false,
      locale = 'en',
      language,
      disabled = false,
      disableSubmitUntilDirty = false,
      getUploadUrl,
      onUploadComplete,
      onFileRemove,
      customComponents,
    },
    ref
  ) => {
    const {
      register,
      setValue,
      getValues,
      handleSubmit,
      formState: { errors },
      reset,
      watch,
      trigger,
      unregister,
    } = useForm({
      reValidateMode: 'onChange',
      mode: 'onBlur',
      defaultValues: getDefaultValues(formFields),
    });

    const [formValues, setFormValues] = useState<FieldValues>();
    const [success, setSuccess] = useState(false);
    const [submissionError, setSubmissionError] = useState<{
      title?: string;
      description?: string;
    }>();
    const [isProcessing, setIsProcessing] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);
    const trackedFields = useRef(new Set<string>());

    // Dirty tracking via lazy pristine snapshot.
    // The snapshot is captured on the FIRST user focus (onFocusCapture),
    // guaranteeing that all field-dependency-driven value changes have
    // already settled. This avoids timing hacks and stabilization loops.
    const pristineSnapshot = useRef<string>('');
    const lastDefaultsHash = useRef<string>('');
    const hasPristineSnapshot = useRef(false);

    // Reset pristine snapshot when form data changes (e.g., API data loads)
    useEffect(() => {
      if (disableSubmitUntilDirty) {
        const defaultsHash = JSON.stringify(getDefaultValues(formFields));
        if (lastDefaultsHash.current !== defaultsHash) {
          lastDefaultsHash.current = defaultsHash;
          hasPristineSnapshot.current = false;
          pristineSnapshot.current = '';
        }
      }
    }, [formFields, disableSubmitUntilDirty]);

    // Capture pristine snapshot on first user focus — by this point field
    // deps have settled, so the snapshot accurately reflects the clean state.
    const capturePristineSnapshot = useCallback(() => {
      if (disableSubmitUntilDirty && !hasPristineSnapshot.current) {
        pristineSnapshot.current = JSON.stringify(getValues());
        hasPristineSnapshot.current = true;
      }
    }, [disableSubmitUntilDirty, getValues]);

    watch(() => {
      if (!hasInteracted) {
        setHasInteracted(true);
        // For forms without disableSubmitUntilDirty, fire onDirtyChange
        // on first interaction (backwards-compatible with slider forms).
        if (!disableSubmitUntilDirty) {
          onDirtyChange?.(true);
        }
      }
      // For forms WITH disableSubmitUntilDirty, use pristine snapshot
      // for accurate dirty state comparison.
      if (
        disableSubmitUntilDirty &&
        hasPristineSnapshot.current &&
        pristineSnapshot.current !== ''
      ) {
        const isDirty = pristineSnapshot.current !== JSON.stringify(getValues());
        onDirtyChange?.(isDirty);
      }
    });

    const watchedValues = watch();
    const formRef = useRef<HTMLDivElement>(null);
    const reCapchaRef = useRef<{ execute: () => void; reset: () => void } | null>(null);

    const HtmlTag = isFluid ? Box : Container;

    useImperativeHandle(ref, () => ({
      resetFormWithValues: (values: FieldValues) => {
        reset(values);
      },
      setValue: (
        name: string,
        value: unknown,
        options?: { shouldValidate?: boolean; shouldDirty?: boolean }
      ) => {
        setValue(name, value, { shouldValidate: true, shouldDirty: true, ...options });
      },
      getValues: <T = unknown,>(name?: string): T => {
        return (name ? getValues(name) : getValues()) as T;
      },
      watch: <T = unknown,>(name?: string): T => {
        return (name ? watch(name) : watch()) as T;
      },
      subscribe: (
        callback: (values: FieldValues, info: { name?: string; type?: string }) => void
      ) => {
        const subscription = watch((values, info) => callback(values, info));
        return subscription;
      },
      validate: (name?: string | string[]) => trigger(name),
    }));

    const onSubmit = async (data: FieldValues) => {
      const formattedData = sanitizeData({ formFields, data, locale });
      setFormValues(formattedData);
      if (reCapchaRef.current) {
        reCapchaRef.current?.execute();
      } else {
        onSuccess && onSuccess({ ...payload, ...formattedData });
      }
    };

    const handleFormSubmit = () => {
      handleSubmit(onSubmit, () => scrollToFirstError(formRef))();
    };

    const onReCAPTCHAVerify = async (token: string | null) => {
      if (!token || !formValues) return;

      if (onSuccess && !apiEndpoint) {
        onSuccess({ ...payload, ...formValues, token });
        return;
      }

      try {
        const attributionData = JSON.parse(window?.sessionStorage?.getItem('attribution') || '{}');
        const attributionHistory = attributionData
          ? UTMAttributionHistory(formValues.email, attributionData)
          : '{"UserID":"n/a"}';

        setIsProcessing(true);
        const response = await fetch(apiEndpoint!, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...payload,
            ...formValues,
            captchaToken: token,
            source: 'Website',
            sourcePlatform: window?.navigator?.platform || null,
            sourceTimeZone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || null,
            language: window?.navigator?.language || null,
            attributionSource: attributionData?.source || null,
            attributionMedium: attributionData?.medium || null,
            attributionCampaign: attributionData?.campaign || null,
            attributionContent: attributionData?.content || null,
            attributionTerm: attributionData?.term || null,
            attributionClientid: attributionData?.clientid || null,
            attributionUserHistory:
              typeof attributionHistory === 'string' ? attributionHistory : null,
            UserID:
              typeof attributionHistory === 'string'
                ? JSON.parse(attributionHistory)?.UserID
                : null,
          }),
        });

        const result = await response.json();

        if (response.ok) {
          setSuccess(true);
          pushToDataLayer({ id, step: 'complete' });
          onSuccess && onSuccess(result);
          reset();
        } else {
          setSuccess(false);
          throw new Error(errorMessage?.title || 'Failed to submit form');
        }
      } catch (error) {
        setSuccess(false);
        if (onError) {
          onError(error);
          return;
        }
        setSubmissionError(errorMessage);
      } finally {
        reCapchaRef.current?.reset();
        setIsProcessing(false);
        setFormValues(undefined);
      }
    };

    const constructFormField = (field: FormField, key: number): React.ReactNode => {
      if (!field.fieldType) {
        return null;
      }

      // Check if field should be hidden via visible prop (defaults to true)
      if ('visible' in field && field.visible === false) {
        return null;
      }

      // Check if field should be shown based on dependency
      const shouldShow =
        !field.dependency || !isEmpty({ fieldType: field.dependency, watchedValues });

      if (!shouldShow) {
        return null;
      }

      switch (field.fieldType) {
        case 'email':
        case 'text':
        case 'number':
          return (
            <InputControl
              field={field as InputField}
              theme={theme}
              direction={direction}
              register={register}
              errors={errors}
              watchedValues={watchedValues}
              setValue={setValue}
              locale={locale}
            />
          );

        case 'textarea':
          return (
            <TextAreaControl
              field={field as TextAreaField}
              theme={theme}
              direction={direction}
              register={register}
              errors={errors}
            />
          );

        case 'phone':
          return (
            <PhoneControl
              field={field as PhoneField}
              theme={theme}
              direction={direction}
              locale={locale}
              register={register}
              errors={errors}
            />
          );

        case 'checkbox':
          return (
            <CheckboxControl
              field={field as CheckboxField}
              theme={theme}
              direction={direction}
              register={register}
              setValue={setValue}
              errors={errors}
            />
          );

        case 'checkbox_group':
          return (
            <CheckboxGroupControl
              field={field as CheckboxGroupField}
              theme={theme}
              direction={direction}
              register={register}
              setValue={setValue}
              watch={watch}
              errors={errors}
            />
          );

        case 'radio_group':
          return (
            <RadioGroupControl
              field={field as RadioGroupField}
              theme={theme}
              direction={direction}
              register={register}
              setValue={setValue}
              watch={watch}
              errors={errors}
              trigger={trigger}
            />
          );

        case 'dropdown':
          return (
            <DropdownControl
              field={field as DropdownField}
              theme={theme}
              direction={direction}
              register={register}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
              errors={errors}
              trigger={trigger}
            />
          );

        case 'date_picker':
          return (
            <DatePickerControl
              field={field as DatePickerField}
              theme={theme}
              direction={direction}
              locale={locale as DatePickerLocale}
              language={language}
              register={register}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
              errors={errors}
              trigger={trigger}
            />
          );

        case 'disclaimers':
          return (
            <DisclaimersControl
              field={field as DisclaimersField}
              theme={theme}
              direction={direction}
            />
          );

        case 'reCaptcha':
          return (
            <ReCaptchaControl
              field={field as ReCaptchaField}
              theme={theme}
              fieldKey={key}
              reCapchaRef={reCapchaRef}
              onCaptchaChange={onReCAPTCHAVerify}
            />
          );

        case 'button':
          return (
            <ButtonControl
              field={field as ButtonField}
              theme={theme}
              watchedValues={watchedValues}
              onSubmit={handleFormSubmit}
            />
          );

        case 'buttonGroup':
          return (
            <ButtonGroupControl
              field={field as ButtonGroupField}
              theme={theme}
              watchedValues={watchedValues}
              formFields={formFields}
              locale={locale}
              onSubmit={handleFormSubmit}
            />
          );

        case 'content':
          return (
            <ContentControl field={field as ContentField} theme={theme} direction={direction} />
          );

        case 'fileUpload':
          if (!getUploadUrl) {
            return null;
          }
          return (
            <FileUploadControl
              field={field as FileUploadField}
              theme={theme}
              register={register}
              setValue={setValue}
              watch={watch}
              errors={errors}
              getUploadUrl={getUploadUrl}
              onUploadComplete={onUploadComplete}
              onFileRemove={onFileRemove}
            />
          );

        case 'group':
          return (
            <GroupControl
              field={field as GroupField}
              theme={theme}
              watchedValues={watchedValues}
              renderField={constructFormField}
            />
          );

        case 'currency':
          return (
            <CurrencyControl
              field={field as CurrencyField}
              theme={theme}
              direction={direction}
              locale={locale}
              register={register}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
              errors={errors}
              trigger={trigger}
            />
          );

        case 'custom':
          return (
            <CustomControl
              field={field as CustomField}
              theme={theme}
              customComponents={customComponents}
              register={register}
              errors={errors}
              onChange={(name, value) => setValue(name, value)}
            />
          );

        case 'typeahead':
          return (
            <TypeaheadControl
              field={field as TypeaheadField}
              theme={theme}
              direction={direction}
              register={register}
              setValue={setValue}
              watch={watch}
              errors={errors}
              trigger={trigger}
            />
          );

        default:
          return null;
      }
    };

    // Track input changes via MutationObserver
    useEffect(() => {
      return observeInputChanges({ formRef, setValue });
    }, [setValue]);

    // Track form start and abandonment
    useEffect(() => {
      if (hasInteracted) {
        pushToDataLayer({ id, step: 'start' });

        const handleBeforeUnload = () =>
          trackProcessAbandon({ id, success, submissionError, trackedFields });

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
          window.removeEventListener('beforeunload', handleBeforeUnload);
        };
      }
      return undefined;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasInteracted]);

    // Track field interactions
    useEffect(() => {
      trackFieldInteraction({ id, watchedValues, trackedFields });
    }, [watchedValues, id]);

    // Handle field dependencies: unregister and reset fields when dependencies change
    useEffect(() => {
      // Group fields by name to handle multiple fields with the same name
      const fieldsByName = new Map<string, FormField[]>();

      formFields.forEach((field) => {
        if ('name' in field && field.name && field.dependency) {
          const existing = fieldsByName.get(field.name) || [];
          fieldsByName.set(field.name, [...existing, field]);
        }
      });

      // For each field name, check which field (if any) should be visible
      fieldsByName.forEach((fields, fieldName) => {
        const visibleField = fields.find((field) => {
          return !isEmpty({ fieldType: field.dependency!, watchedValues });
        });

        if (!visibleField) {
          // Only unregister if the field is currently in the form state.
          // Calling unregister() on an already-unregistered field still notifies
          // RHF's watch() subscribers, which would change watchedValues and
          // re-trigger this effect — causing an infinite render loop.
          if (fieldName in getValues()) {
            unregister(fieldName);
          }
        } else {
          // A field is visible - check if it's a different variant than before
          // by checking if the current value is valid for the visible field's options
          const currentValue = watchedValues[fieldName];

          if (currentValue && 'options' in visibleField && visibleField.options) {
            const isValidOption = visibleField.options.some((opt) => {
              // Handle grouped options
              if ('data' in opt) {
                return opt.data.some((item) => item.value === currentValue);
              }
              // Handle regular options
              return 'value' in opt && opt.value === currentValue;
            });

            // If current value is not valid for the visible field, reset it
            // Only call setValue if the value actually needs to change to prevent infinite loops
            if (!isValidOption && currentValue !== '') {
              setValue(fieldName, '');
            }
          }
        }
      });

      // Unregister named children of group fields whose dependency is not met.
      // react-hook-form does not auto-unregister on unmount (shouldUnregister defaults to
      // false), so hidden group children remain registered and their required/validate rules
      // would otherwise block form submission.
      // The same getValues() guard is applied to prevent infinite re-render loops.
      const currentValues = getValues();
      formFields.forEach((field) => {
        if (field.fieldType === 'group' && field.dependency) {
          const groupVisible = !isEmpty({ fieldType: field.dependency, watchedValues });
          if (!groupVisible) {
            (field as GroupField).children.forEach((childField) => {
              if ('name' in childField && childField.name && childField.name in currentValues) {
                unregister(childField.name);
              }
            });
          }
        }
      });
    }, [watchedValues, formFields, unregister, setValue, getValues]);

    const isFormDirty = disableSubmitUntilDirty
      ? hasPristineSnapshot.current &&
        pristineSnapshot.current !== '' &&
        pristineSnapshot.current !== JSON.stringify(getValues())
      : false;

    useEffect(() => {
      if (disableSubmitUntilDirty) {
        onDirtyChange?.(isFormDirty);
      }
    }, [isFormDirty, disableSubmitUntilDirty, onDirtyChange]);

    const submitDisabled = disableSubmitUntilDirty && !isFormDirty;
    const formContextValue = useMemo(
      () => ({ disabled, submitDisabled }),
      [disabled, submitDisabled]
    );

    return (
      <FormContext.Provider value={formContextValue}>
        <HtmlTag>
          <Box
            id={id}
            className={classnames(styles.formContainer, styles[alignment], {
              [styles.isFluid]: isFluid,
            })}
            theme={theme}
            ref={formRef}
            onFocusCapture={capturePristineSnapshot}
            {...(!isFluid ? { pb: { xs: 40, md: 48, xl: 64 } } : {})}
          >
            {isProcessing && <Loader size={48} theme={theme} />}

            {success && (
              <Box className={styles.successContainer} role="alert">
                <Spec
                  title={successMessage?.title}
                  description={successMessage?.description}
                  alignment={alignment}
                  theme={theme}
                />
              </Box>
            )}

            {!success && submissionError && (
              <Box className={styles.errorContainer} role="alert">
                <Spec
                  title={submissionError?.title}
                  description={submissionError?.description}
                  theme={theme}
                />
              </Box>
            )}

            {!success && !submissionError && !isProcessing && (
              <Box className={styles.container}>
                <Box className={styles.grid}>
                  {formFields.map((field, i) => {
                    const effectiveWidth = getEffectiveWidth({ field, watchedValues });
                    return (
                      <Box
                        key={field.id}
                        className={classnames(styles.wrapper, styles[effectiveWidth])}
                      >
                        {constructFormField(field, i)}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </HtmlTag>
      </FormContext.Provider>
    );
  }
);

Form.displayName = 'Form';

export default Form;
